import { FC, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { OptionsHelper } from '_metronic/helpers'
import { showConfirmAlert } from '_metronic/helpers/AlertHelper'
import { useT } from '_metronic/i18n/Metronici18n'
import { FormSelect, FormTextArea, IOption, useModal, useToast } from 'app/components'
import { MotivoBaixaDispositivo, MotivoFimAlocacao } from '../../../dispositivos/core/_models'
import { opcoesMotivoBaixa, opcoesMotivoFim } from '../../../dispositivos/core/rotulos'
import { trataErroDoModal } from '../../../dispositivos/core/errosFormulario'
import { textoOuNulo } from '../../../dispositivos/core/textoOuNulo'
import { camposDicomSchema } from '../../../dispositivos/components/modal/validation/dispositivoSchema'
import { TelekitDispositivoList } from '../../telekits-list/core/_models'
import { desalocarDispositivo } from '../../telekits-list/core/_requests'
import { ErrosGerais } from '../../../dispositivos/core/ErrosGerais'
import { RodapeModal } from '../../../dispositivos/core/RodapeModal'

type Valores = {
  motivoFim?: IOption<MotivoFimAlocacao>
  observacao: string
  empresaDestino?: IOption<number>
  motivoBaixa?: IOption<MotivoBaixaDispositivo>
}

const CAMPOS = ['motivoFim', 'observacao', 'empresaDestino', 'motivoBaixa'] as const
const MAPA = { idEmpresaDestino: 'empresaDestino' }

export const DesalocarDispositivoModal: FC<{
  idTelekit: number
  alocacao: TelekitDispositivoList
}> = ({ idTelekit, alocacao }) => {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const { t } = useT()
  const [erroGeral, setErroGeral] = useState<string[]>([])
  const vendido = alocacao.dispositivo.regime === 'VENDIDO'
  const empresaMaleta = alocacao.empresaAlocacao
  const opcoesFim = useMemo(() => opcoesMotivoFim(t), [t])
  const opcoesBaixa = useMemo(() => opcoesMotivoBaixa(t), [t])

  const formik = useFormik<Valores>({
    initialValues: {
      motivoFim: undefined,
      observacao: '',
      // Custódia atual do aparelho alocado = empresa da maleta.
      empresaDestino: empresaMaleta
        ? { value: empresaMaleta.id, label: empresaMaleta.nome }
        : undefined,
      motivoBaixa: undefined,
    },
    validationSchema: Yup.object().shape({
      motivoFim: Yup.object()
        .nullable()
        .required(t('pagina.telekits.dispositivos.validacao.motivo_fim')),
      observacao: camposDicomSchema(t).observacao,
      motivoBaixa: Yup.object()
        .nullable()
        .when('motivoFim', {
          is: (motivo?: IOption<MotivoFimAlocacao>) => motivo?.value === 'BAIXA',
          then: (schema) => schema.required(t('pagina.dispositivos.validacao.motivo_baixa')),
        }),
    }),
    onSubmit: async (valores, helpers) => {
      const motivoFim = valores.motivoFim?.value
      if (!motivoFim) return
      setErroGeral([])
      if (motivoFim === 'BAIXA') {
        const confirmado = await showConfirmAlert({
          title: t('pagina.telekits.dispositivos.baixa_titulo'),
          text: t('pagina.telekits.dispositivos.baixa_texto'),
          confirmButtonText: t('pagina.telekits.dispositivos.baixa_confirmar'),
          cancelButtonText: t('common.cancel'),
          danger: true,
        })
        if (!confirmado) return
      }
      try {
        await desalocarDispositivo(idTelekit, alocacao.id, {
          motivoFim,
          observacao: textoOuNulo(valores.observacao),
          idEmpresaDestino: vendido ? null : valores.empresaDestino?.value ?? null,
          motivoBaixa: motivoFim === 'BAIXA' ? valores.motivoBaixa?.value ?? null : null,
        })
        showToast({ variant: 'success', title: t('pagina.telekits.dispositivos.desalocado') })
        closeModal()
      } catch (error) {
        trataErroDoModal(error, helpers, CAMPOS, setErroGeral, MAPA)
      }
    },
  })

  const { setFieldValue } = formik
  const motivo = formik.values.motivoFim?.value

  const aoTrocarMotivo = () => setFieldValue('motivoBaixa', undefined)

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <ErrosGerais erros={erroGeral} />
      <div className='mb-5'>
        <FormSelect
          formik={formik}
          name='motivoFim'
          label={t('pagina.telekits.dispositivos.campo.motivo_fim')}
          inputId='desalocar-motivo'
          options={opcoesFim}
          isClearable={false}
          isRequired
          onChange={aoTrocarMotivo}
        />
        {motivo && (
          <div className='form-text text-gray-700' role='status'>
            {t(`pagina.telekits.dispositivos.consequencia.${motivo}`)}
          </div>
        )}
      </div>
      {motivo === 'BAIXA' && (
        <div className='mb-5'>
          <FormSelect
            formik={formik}
            name='motivoBaixa'
            label={t('pagina.dispositivos.campo.motivo_baixa')}
            inputId='desalocar-motivo-baixa'
            options={opcoesBaixa}
            isClearable={false}
            isRequired
          />
        </div>
      )}
      <div className='mb-5'>
        <FormSelect
          formik={formik}
          name='empresaDestino'
          label={t('pagina.telekits.dispositivos.campo.empresa_destino')}
          inputId='desalocar-empresa-destino'
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          fetchOptionsParams={{ onlyActive: false }}
          isClearable={false}
          isDisabled={vendido}
        />
        <div className='form-text text-gray-700'>
          {vendido
            ? t('pagina.telekits.dispositivos.destino_vendido')
            : t('pagina.telekits.dispositivos.ajuda_destino')}
        </div>
      </div>
      <FormTextArea
        formik={formik}
        name='observacao'
        label={t('pagina.telekits.dispositivos.campo.observacao')}
        rows={3}
      />
      <RodapeModal
        enviando={formik.isSubmitting}
        rotuloEnviar={t('pagina.telekits.dispositivos.desalocar')}
        classeEnviar='btn-danger'
      />
    </form>
  )
}
